// frontend/shared-ui/src/forms/schemas/instrument.js

// ─── Type classification ──────────────────────────────────────────────
const MICROSCOPE_TYPES = [
    'Fiber Optic Microscope',
    'Fiber Inspection Scope',
    'Video Inspection Probe',
    'Handheld Microscope',
];

const OTDR_TYPES = ['OTDR'];
const POWER_METER_TYPES = ['Optical Power Meter'];
const LIGHT_SOURCE_TYPES = ['Light Source'];
const VFL_TYPES = ['VFL'];
const SPLICER_TYPES = ['Fusion Splicer'];
const IDENTIFIER_TYPES = ['Fiber Identifier'];
const LOSS_TESTER_TYPES = ['Optical Loss Tester'];

const isOneOf = (v, list) => list.includes(v && v.instrumentType);
const isMicroscope = (v) => isOneOf(v, MICROSCOPE_TYPES);
const isOtdr = (v) => isOneOf(v, OTDR_TYPES);
const isPowerMeter = (v) => isOneOf(v, POWER_METER_TYPES);
const isLightSource = (v) => isOneOf(v, LIGHT_SOURCE_TYPES);
const isVfl = (v) => isOneOf(v, VFL_TYPES);
const isSplicer = (v) => isOneOf(v, SPLICER_TYPES);
const isIdentifier = (v) => isOneOf(v, IDENTIFIER_TYPES);
const isLossTester = (v) => isOneOf(v, LOSS_TESTER_TYPES);

// ─── Static options ───────────────────────────────────────────────────
const MAGNIFICATION_OPTIONS = [
    { value: '200×', label: '200×' },
    { value: '400×', label: '400×' },
    { value: '200× / 400×', label: '200× / 400× (switchable)' },
    { value: '250×', label: '250×' },
    { value: '500×', label: '500×' },
];

const INSPECTION_TYPE_OPTIONS = [
    { value: 'Handheld', label: 'Handheld (portable)' },
    { value: 'Desktop', label: 'Desktop (bench)' },
    { value: 'Video Probe', label: 'Video inspection probe' },
    { value: 'USB Probe', label: 'USB inspection probe' },
];

const FOCUS_TYPE_OPTIONS = [
    { value: 'Manual', label: 'Manual focus' },
    { value: 'Auto-focus', label: 'Auto-focus' },
    { value: 'Auto-focus + Auto-centering', label: 'Auto-focus + auto-centering' },
];

const DISPLAY_TYPE_OPTIONS = [
    { value: 'None', label: 'No display (via PC/phone)' },
    { value: '2.4"', label: '2.4" LCD' },
    { value: '3.5"', label: '3.5" LCD' },
    { value: '5"', label: '5" IPS LCD' },
    { value: '7"', label: '7" IPS LCD' },
    { value: 'External', label: 'External monitor' },
];

const ANALYSIS_OPTIONS = [
    { value: 'Visual only', label: 'Visual only' },
    { value: 'IEC 61300-3-35 auto pass/fail', label: 'IEC 61300-3-35 auto pass/fail' },
    { value: 'IEC 61300-3-35 + auto-detect zone', label: 'IEC 61300-3-35 + auto-detect zone' },
    { value: 'Certified + report generation', label: 'Certified + report generation' },
];

const CONNECTOR_ADAPTERS = [
    'SC', 'LC', 'FC', 'ST', 'MU', 'E2000', 'MPO', 'MTP', 'CS', 'SN',
];

const LIGHT_SOURCE_OPTIONS = [
    { value: 'LED', label: 'LED' },
    { value: 'White light', label: 'White light' },
    { value: 'Infrared (IR)', label: 'Infrared (IR)' },
];

// ─── Schema ───────────────────────────────────────────────────────────
export default {
    slug: 'instrument',
    label: 'Test Instruments & Microscopes',
    defaultUnit: 'pcs',
    costDefaults: {
        avgVolumeCbm: 0.025,
        suggestedMarkupPct: 25,
        freightSensitivity: 'medium',
        note: 'OTDRs 0.024, splicers 0.04 — expensive items where freight is small % of FOB',
    },
    

    groups: [
        // ──────────────────────────────────────────────────────────────
        // 1. Basic
        // ──────────────────────────────────────────────────────────────
        {
            id: 'basic',
            label: 'Basic',
            columns: 2,
            fields: [
                {
                    key: 'instrumentType',
                    label: 'Instrument Type',
                    type: 'select',
                    required: true,
                    options: [
                        'OTDR',
                        'Optical Power Meter',
                        'Light Source',
                        'VFL',
                        'Fusion Splicer',
                        'Fiber Identifier',
                        'Optical Loss Tester',
                        'Fiber Optic Microscope',
                        'Fiber Inspection Scope',
                        'Video Inspection Probe',
                        'Handheld Microscope',
                    ],
                },
                {
                    key: 'wavelengths',
                    label: 'Wavelengths',
                    type: 'text',
                    placeholder: '1310 / 1550 nm — or N/A for microscopes',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 2. MICROSCOPE SPECIFIC — only when instrumentType is a scope
        // ──────────────────────────────────────────────────────────────
        {
            id: 'microscope',
            label: 'Fiber Inspection Microscope Specifications',
            columns: 2,
            when: isMicroscope,
            fields: [
                {
                    key: 'inspectionType',
                    label: 'Inspection Type',
                    type: 'select',
                    required: true,
                    options: INSPECTION_TYPE_OPTIONS,
                },
                {
                    key: 'magnification',
                    label: 'Magnification',
                    type: 'select',
                    required: true,
                    options: MAGNIFICATION_OPTIONS,
                },
                {
                    key: 'focusType',
                    label: 'Focus Type',
                    type: 'select',
                    options: FOCUS_TYPE_OPTIONS,
                },
                {
                    key: 'displayType',
                    label: 'Display',
                    type: 'select',
                    options: DISPLAY_TYPE_OPTIONS,
                },
                {
                    key: 'analysis',
                    label: 'Analysis / Pass-Fail',
                    type: 'select',
                    options: ANALYSIS_OPTIONS,
                },
                {
                    key: 'illumination',
                    label: 'Illumination',
                    type: 'select',
                    options: LIGHT_SOURCE_OPTIONS,
                },
                {
                    key: 'connectorAdapters',
                    label: 'Included Adapters',
                    type: 'text',
                    placeholder: 'SC, LC, FC, MU',
                    hint: 'Comma-separated list of connector adapters in the box',
                },
                {
                    key: 'reportGeneration',
                    label: 'Generates Inspection Report',
                    type: 'boolean',
                },
                {
                    key: 'imageCapture',
                    label: 'Image / Video Capture',
                    type: 'boolean',
                },
                {
                    key: 'wirelessConnection',
                    label: 'Wi-Fi / Bluetooth',
                    type: 'boolean',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 3. OTDR / MEASUREMENT — only when relevant
        // ──────────────────────────────────────────────────────────────
        {
            id: 'measurement',
            label: 'Measurement',
            columns: 3,
            when: (v) => isOtdr(v) || isPowerMeter(v) || isLightSource(v) || isLossTester(v),
            fields: [
                {
                    key: 'dynamicRange',
                    label: 'Dynamic Range',
                    type: 'number',
                    unit: 'dB',
                },
                {
                    key: 'distanceRange',
                    label: 'Distance Range',
                    type: 'text',
                    placeholder: '0.1 – 240 km',
                },
                {
                    key: 'eventDeadZone',
                    label: 'Event Dead Zone',
                    type: 'number',
                    unit: 'm',
                    step: 0.1,
                },
                {
                    key: 'attenuationDeadZone',
                    label: 'Attenuation Dead Zone',
                    type: 'number',
                    unit: 'm',
                    step: 0.1,
                },
                {
                    key: 'accuracy',
                    label: 'Accuracy',
                    type: 'text',
                    placeholder: '±0.05 dB',
                },
                {
                    key: 'resolution',
                    label: 'Resolution',
                    type: 'text',
                    placeholder: '0.01 dB',
                },
                {
                    key: 'wavelengthsDetail',
                    label: 'Wavelength Detail',
                    type: 'text',
                    placeholder: '1310 / 1550 / 1625 nm',
                },
                {
                    key: 'detectorType',
                    label: 'Detector Type',
                    type: 'text',
                    placeholder: 'InGaAs',
                },
                {
                    key: 'toneDetection',
                    label: 'Tone Detection (270 Hz / 1 kHz / 2 kHz)',
                    type: 'boolean',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 4. FUSION SPLICER — only when relevant
        // ──────────────────────────────────────────────────────────────
        {
            id: 'splicer',
            label: 'Fusion Splicer Specifications',
            columns: 2,
            when: isSplicer,
            fields: [
                {
                    key: 'spliceLoss',
                    label: 'Typical Splice Loss',
                    type: 'number',
                    unit: 'dB',
                    step: 0.01,
                },
                {
                    key: 'spliceTime',
                    label: 'Typical Splice Time',
                    type: 'number',
                    unit: 's',
                    step: 0.1,
                },
                {
                    key: 'arcPower',
                    label: 'Arc Power Range',
                    type: 'text',
                    placeholder: 'e.g. 10–60',
                },
                {
                    key: 'fibersSupported',
                    label: 'Fibers Supported',
                    type: 'text',
                    placeholder: 'SM, MM, DS, NZDS',
                },
                {
                    key: 'batterySplices',
                    label: 'Battery Life (splices)',
                    type: 'number',
                    min: 0,
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 5. Physical / General
        // ──────────────────────────────────────────────────────────────
        {
            id: 'physical',
            label: 'Physical & Connectivity',
            columns: 3,
            fields: [
                {
                    key: 'display',
                    label: 'Display',
                    type: 'text',
                    placeholder: '7" touchscreen / none',
                },
                {
                    key: 'battery',
                    label: 'Battery Life',
                    type: 'text',
                    placeholder: '10 h',
                },
                {
                    key: 'weight_g',
                    label: 'Weight',
                    type: 'number',
                    unit: 'g',
                },
                {
                    key: 'interfaces',
                    label: 'Interfaces',
                    type: 'text',
                    placeholder: 'USB, Wi-Fi, Bluetooth',
                },
                {
                    key: 'storage',
                    label: 'Storage',
                    type: 'text',
                    placeholder: '16 GB / SD',
                },
                {
                    key: 'operatingTemp',
                    label: 'Operating Temperature',
                    type: 'text',
                    placeholder: '-10 to +50 °C',
                },
            ],
        },
    ],

    // ─── SKU ─────────────────────────────────────────────────────────────
    buildSku(v) {
        const codes = {
            'OTDR': 'OTDR',
            'Optical Power Meter': 'OPM',
            'Light Source': 'LS',
            'VFL': 'VFL',
            'Fusion Splicer': 'FSPL',
            'Fiber Identifier': 'FID',
            'Optical Loss Tester': 'OLT',
            'Fiber Optic Microscope': 'FOM',
            'Fiber Inspection Scope': 'FIS',
            'Video Inspection Probe': 'VIP',
            'Handheld Microscope': 'HHM',
        };
        const code = codes[v.instrumentType] || 'INSTR';

        const parts = ['INSTR', code];

        // Microscope-specific SKU parts
        if (isMicroscope(v)) {
            if (v.magnification) {
                parts.push(v.magnification.replace(/[×\s/]+/g, 'X'));
            }
            if (v.inspectionType) {
                const t = v.inspectionType.replace(/\s+/g, '').toUpperCase();
                parts.push(t);
            }
            if (v.focusType === 'Auto-focus + Auto-centering') parts.push('AFC');
            else if (v.focusType === 'Auto-focus') parts.push('AF');
            if (v.analysis && /IEC/.test(v.analysis)) parts.push('IEC');
            if (v.reportGeneration) parts.push('RPT');
        } else {
            // Measuring instruments
            if (v.wavelengths) {
                parts.push(v.wavelengths.replace(/\s+/g, '').replace(/\//g, '-'));
            }
            if (v.dynamicRange) parts.push(`${v.dynamicRange}DB`);
        }

        return parts
            .filter(Boolean)
            .join('-')
            .toUpperCase()
            .replace(/[^A-Z0-9\-×]/g, '');
    },

    // ─── Name ────────────────────────────────────────────────────────────
    buildName(v) {
        const bits = [];

        if (!v.instrumentType) return 'Test Instrument';

        // ── Microscope branch ────────────────────────────────────────
        if (isMicroscope(v)) {
            if (v.magnification) bits.push(v.magnification);
            bits.push(v.instrumentType);
            if (v.inspectionType) bits.push(`(${v.inspectionType})`);
            if (v.focusType) bits.push(`— ${v.focusType}`);
            if (v.analysis && /IEC/.test(v.analysis)) bits.push('· IEC 61300-3-35');
            if (v.connectorAdapters) bits.push(`· Adapters: ${v.connectorAdapters}`);
            return bits.filter(Boolean).join(' ').trim();
        }

        // ── Measurement-instrument branch ────────────────────────────
        if (v.wavelengths) bits.push(v.wavelengths);
        bits.push(v.instrumentType);
        if (v.dynamicRange) bits.push(`${v.dynamicRange}dB dynamic range`);
        if (v.fibersSupported) bits.push(`(${v.fibersSupported})`);

        return bits.filter(Boolean).join(' ').trim();
    },

    // ─── Detail view ────────────────────────────────────────────────────
    attributeSchema: [
        { key: 'instrumentType', label: 'Instrument Type' },
        { key: 'wavelengths', label: 'Wavelengths' },

        // Microscope
        { key: 'inspectionType', label: 'Inspection Type' },
        { key: 'magnification', label: 'Magnification' },
        { key: 'focusType', label: 'Focus Type' },
        { key: 'displayType', label: 'Display' },
        { key: 'analysis', label: 'Analysis' },
        { key: 'illumination', label: 'Illumination' },
        { key: 'connectorAdapters', label: 'Connector Adapters' },
        { key: 'reportGeneration', label: 'Report Generation' },
        { key: 'imageCapture', label: 'Image / Video Capture' },
        { key: 'wirelessConnection', label: 'Wi-Fi / Bluetooth' },

        // Measurement instruments
        { key: 'dynamicRange', label: 'Dynamic Range', unit: 'dB' },
        { key: 'distanceRange', label: 'Distance Range' },
        { key: 'eventDeadZone', label: 'Event Dead Zone', unit: 'm' },
        { key: 'attenuationDeadZone', label: 'Attenuation Dead Zone', unit: 'm' },
        { key: 'accuracy', label: 'Accuracy' },
        { key: 'resolution', label: 'Resolution' },
        { key: 'detectorType', label: 'Detector Type' },
        { key: 'toneDetection', label: 'Tone Detection' },

        // Fusion splicer
        { key: 'spliceLoss', label: 'Splice Loss', unit: 'dB' },
        { key: 'spliceTime', label: 'Splice Time', unit: 's' },
        { key: 'arcPower', label: 'Arc Power Range' },
        { key: 'fibersSupported', label: 'Fibers Supported' },
        { key: 'batterySplices', label: 'Battery Life (splices)' },

        // Physical
        { key: 'display', label: 'Display' },
        { key: 'battery', label: 'Battery Life' },
        { key: 'weight_g', label: 'Weight', unit: 'g' },
        { key: 'interfaces', label: 'Interfaces' },
        { key: 'storage', label: 'Storage' },
        { key: 'operatingTemp', label: 'Operating Temperature' },
    ],
};